import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import debug from 'debug';
import { Fragment, useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { Button } from '@/components/ui/button';
import { isArabic } from '@/config/constants';
import { isWidgetExpandedState } from '@/state/session';
const log = debug('moveo:card');
export const CarouselCard = ({ card, onPostback, className, disabled, onWebviewClick, onButtonClick, active = false, }) => {
    const { media, buttons, title, subtitle } = card;
    const isWebchatExpanded = useRecoilValue(isWidgetExpandedState);
    const resolvedMedia = useMemo(() => {
        switch (media.type) {
            case 'image':
                return (_jsx("img", { src: media.url, alt: title, className: "h-full w-full object-cover" }));
            case 'video':
                // eslint-disable-next-line jsx-a11y/media-has-caption
                return (_jsx("video", { src: media.url, controls: true, className: "h-full w-full object-cover", children: _jsx("track", { kind: "captions" }) }));
            default:
                return null;
        }
    }, [media.type, media.url, title]);
    const getButtonProps = useCallback((button) => {
        const baseButtonClasses = cn('mt-2 inline-block overflow-hidden text-center text-ellipsis whitespace-nowrap transition-colors duration-250');
        const handleClick = () => {
            const eventType = `carousel_button_${button.type}`;
            let eventValue;
            switch (button.type) {
                case 'url':
                case 'webview':
                    eventValue = button.url;
                    break;
                case 'postback':
                case 'phone':
                    eventValue = button.value;
                    break;
                default:
                    eventValue = '';
            }
            onButtonClick({
                type: eventType,
                label: button.label,
                value: eventValue,
            });
            if (button.type === 'postback' && onPostback) {
                onPostback(button.value, button.label);
            }
            else if (button.type === 'webview' && onWebviewClick) {
                onWebviewClick(button);
            }
        };
        const buttonProps = {
            dir: isArabic(button.label),
            className: baseButtonClasses,
            size: 'sm',
            disabled,
            children: button.label,
            variant: 'secondary',
        };
        switch (button.type) {
            case 'postback':
                return _jsx(Button, Object.assign({}, buttonProps, { onClick: handleClick }));
            case 'url':
                return (_jsx(Button, Object.assign({}, buttonProps, { onClick: (e) => {
                        e.preventDefault();
                        window.open(button.url, '_blank', 'noreferrer');
                        handleClick();
                    } })));
            case 'phone':
                return (_jsx(Button, Object.assign({}, buttonProps, { onClick: (e) => {
                        e.preventDefault();
                        window.location.href = `tel:${button.value}`;
                        handleClick();
                    } })));
            case 'webview':
                return _jsx(Button, Object.assign({}, buttonProps, { onClick: handleClick }));
            default:
                log(`Unsupported button type: ${button}`, button);
                return null;
        }
    }, [disabled, onButtonClick, onPostback, onWebviewClick]);
    return (_jsxs("div", { className: cn('dark:border-input flex h-full flex-col justify-between overflow-hidden rounded-xl border border-gray-200', {
            'opacity-30': !active,
        }, className), children: [_jsxs("div", { className: "flex-grow", children: [_jsx("div", { className: cn('h-40 w-full', {
                            'h-45': isWebchatExpanded,
                        }), children: resolvedMedia }), _jsxs("div", { className: "px-4 pt-2", children: [_jsx("h4", { className: "text-foreground mb-1.5 line-clamp-2 overflow-hidden text-lg leading-7 font-medium", children: title }), _jsx("p", { className: "text-foreground-muted m-0 text-sm leading-5", children: subtitle })] })] }), _jsx("div", { className: "mt-1.5 flex flex-col justify-end px-4 pb-4", children: buttons.map((button, i) => (_jsx(Fragment, { children: getButtonProps(button) }, `${i}_${button.type}`))) })] }));
};
